import { AKComponent } from '@theme/component';

function supportsHover() {
  return window.matchMedia('(hover: hover)').matches;
}

function inView(element, callback) {
  if (typeof IntersectionObserver === 'undefined') {
    callback();
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        callback();
        observer.disconnect();
      }
    },
    { rootMargin: '0px', threshold: 0 }
  );
  observer.observe(element);
}

export class BeforeAfterComponent extends AKComponent {
  constructor() {
    super();
    this._onPointerMoveListener = this._onPointerMove.bind(this);
    this._touchStartTimestamp = 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this._onPointerDown.bind(this));
    this.addEventListener('keydown', this._onKeyboardNavigation.bind(this));
    this.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: false });

    inView(this, () => {
      this._animateInitialPosition();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('pointermove', this._onPointerMoveListener);
    super.disconnectedCallback?.();
  }

  _onPointerDown(event) {
    const target = event.target;
    if (target instanceof Element && target.closest('a')) return;

    // ✅ stopImmediatePropagation bloque TOUS les listeners sur le scroller du slideshow
    event.stopImmediatePropagation();
    event.preventDefault();

    document.addEventListener('pointerup', (e) => this._onPointerUp(e), { once: true });
    document.addEventListener('pointermove', this._onPointerMoveListener);
    this._calculatePosition(event);
  }

  _onPointerMove(event) {
    // ✅ Empêche le slideshow de capter le mouvement
    event.stopImmediatePropagation();
    this._calculatePosition(event);
    this.classList.add('is-dragging');
  }

  _onTouchStart(event) {
    const cursor = this.querySelector('.c-before-after__cursor');
    const target = event.target;
    if (cursor && target instanceof Node && (target === cursor || cursor.contains(target))) {
      // ✅ Sur le curseur : bloque tout et démarre le drag
      event.preventDefault();
      event.stopImmediatePropagation();
      document.addEventListener('pointermove', this._onPointerMoveListener);

      const stopTouchMove = (e) => {
        e.stopImmediatePropagation();
        e.stopPropagation();
      };
      document.addEventListener('touchmove', stopTouchMove, { passive: false });
      document.addEventListener('pointerup', () => {
        document.removeEventListener('touchmove', stopTouchMove);
      }, { once: true });
    } else {
      this._touchStartTimestamp = event.timeStamp;
    }
  }

  _onPointerUp(event) {
    document.removeEventListener('pointermove', this._onPointerMoveListener);
    this.classList.remove('is-dragging');
    if (!supportsHover()) {
      if (event.timeStamp - this._touchStartTimestamp <= 250) {
        this._calculatePosition(event);
      }
    }
  }

  _onKeyboardNavigation(event) {
    const cursorRef = this.refs?.cursor ?? this.querySelector('.c-before-after__cursor');
    const cursor = Array.isArray(cursorRef) ? cursorRef[0] : cursorRef;
    const target = event.target;
    if (
      !cursor ||
      !(target instanceof Node && cursor.contains(target)) ||
      (this.hasAttribute('vertical') && event.code !== 'ArrowUp' && event.code !== 'ArrowDown') ||
      (!this.hasAttribute('vertical') && event.code !== 'ArrowLeft' && event.code !== 'ArrowRight')
    ) {
      return;
    }
    event.preventDefault();
    let currentPosition = parseInt(
      this.style.getPropertyValue('--before-after-cursor-position') ||
        getComputedStyle(this).getPropertyValue('--before-after-initial-cursor-position'),
      10
    );
    if (Number.isNaN(currentPosition)) currentPosition = 50;

    let newPosition;
    if (this.hasAttribute('vertical')) {
      newPosition = event.code === 'ArrowUp' ? currentPosition - 1 : currentPosition + 1;
    } else {
      newPosition = event.code === 'ArrowLeft' ? currentPosition - 1 : currentPosition + 1;
    }
    this._setPosition(Math.min(Math.max(newPosition, 0), 100));
  }

  _calculatePosition(event) {
    const rect = this.getBoundingClientRect();
    let percentage;
    if (this.hasAttribute('vertical')) {
      percentage = ((event.clientY - rect.top) / this.clientHeight) * 100;
    } else {
      percentage = ((event.clientX - rect.left) / this.clientWidth) * 100;
      if (document.documentElement.dir === 'rtl') {
        percentage = 100 - percentage;
      }
    }
    this._setPosition(Math.min(Math.max(percentage, 0), 100));
  }

  _setPosition(value) {
    this.style.setProperty('--before-after-cursor-position', `${value}%`);
    const cursorRef = this.refs?.cursor ?? this.querySelector('.c-before-after__cursor');
    const cursor = Array.isArray(cursorRef) ? cursorRef[0] : cursorRef;
    if (cursor instanceof Element) {
      cursor.setAttribute('aria-valuenow', `${Math.round(value)}`);
    }
  }

  _animateInitialPosition() {
    this.style.setProperty(
      '--before-after-cursor-position',
      'var(--before-after-initial-cursor-position)'
    );
  }
}

if (!window.customElements.get('before-after')) {
  window.customElements.define('before-after', BeforeAfterComponent);
}